/**
 * Lightweight browser & in-app environment detection.
 * Pure functions, no side effects, safe to import anywhere.
 */

const ua = (): string =>
  (typeof navigator !== 'undefined' && navigator.userAgent) || '';

export function isFacebookInApp(): boolean {
  return /FBAN|FBAV|FB_IAB|FB4A/i.test(ua());
}

export function isInstagramInApp(): boolean {
  return /Instagram/i.test(ua());
}

export function isTikTokInApp(): boolean {
  return /TikTok|musical_ly|Bytedance/i.test(ua());
}

export function isLineInApp(): boolean {
  return /Line\//i.test(ua());
}

export function isTwitterInApp(): boolean {
  return /Twitter/i.test(ua());
}

export function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(ua());
}

export function isAndroid(): boolean {
  return /Android/i.test(ua());
}

/** Heuristic for generic Android WebView (no "Chrome/" or "wv" marker). */
export function isAndroidWebView(): boolean {
  const u = ua();
  if (!/Android/i.test(u)) return false;
  return /; wv\)/i.test(u) || (!/Chrome\//i.test(u) && /Version\//i.test(u));
}

/** Heuristic for iOS WebView (no Safari/ marker on an iOS device). */
export function isIOSWebView(): boolean {
  if (!isIOS()) return false;
  const u = ua();
  return !/Safari\//i.test(u);
}

export function isInAppBrowser(): boolean {
  return (
    isFacebookInApp() ||
    isInstagramInApp() ||
    isTikTokInApp() ||
    isLineInApp() ||
    isTwitterInApp() ||
    isAndroidWebView() ||
    isIOSWebView()
  );
}

export function getBrowserLabel(): string {
  if (isFacebookInApp()) return 'facebook-inapp';
  if (isInstagramInApp()) return 'instagram-inapp';
  if (isTikTokInApp()) return 'tiktok-inapp';
  if (isLineInApp()) return 'line-inapp';
  if (isTwitterInApp()) return 'twitter-inapp';
  if (isAndroidWebView()) return 'android-webview';
  if (isIOSWebView()) return 'ios-webview';
  if (isIOS()) return 'ios-safari';
  if (isAndroid()) return 'android-chrome';
  return 'desktop';
}

/** Returns a URL that asks the OS to open the current page in the real browser. */
export function externalBrowserUrl(): string {
  if (typeof window === 'undefined') return '';
  const url = window.location.href;
  if (isAndroid()) {
    // Intent URL — opens in Chrome on Android.
    const stripped = url.replace(/^https?:\/\//, '');
    return `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`;
  }
  if (isIOS()) {
    // x-safari-https:// — opens in Safari on iOS.
    return url.replace(/^https:\/\//, 'x-safari-https://').replace(/^http:\/\//, 'x-safari-http://');
  }
  return url;
}
